import React, { useEffect, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ChangeRequestModule, Modules } from "../../constants/Module";
import { Action } from "../../constants/Common";
import dayjs from "dayjs";
import { Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";

const ScheduleModule = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [newTaskObject, setNewTaskObject] = useState({
    currentStartDate: props.project?.startDate
      ? dayjs(props.project?.startDate).format("YYYY-MM-DD")
      : null,
    currentEndDate: props.project?.endDate
      ? dayjs(props.project?.endDate).format("YYYY-MM-DD")
      : null,
    startDate: props?.projectMeta?.schedule?.startDate?.new ?? "",
    endDate: props?.projectMeta?.schedule?.endDate?.new ?? "",
  });

  const taskValidation = Yup.object().shape(
    {
      currentStartDate: Yup.date().optional().nullable(),
      currentEndDate: Yup.date().optional().nullable(),
      startDate: Yup.date()
        .typeError(props.t("Given date is invalid type"))
        .when("endDate", {
          is: (endDate) => !!endDate,
          then: (schema) =>
            schema.max(
              Yup.ref("endDate"),
              props.t("Start Date should be less than End Date.")
            ),
          otherwise: Yup.date().when("currentEndDate", {
            is: (currentEndDate) => !!currentEndDate,
            then: (schema) =>
              schema.max(
                Yup.ref("currentEndDate"),
                props.t("Start Date should be less than project End Date.")
              ),
            otherwise: Yup.date().nullable().optional(),
          }),
        }),
      endDate: Yup.date().when("startDate", {
        is: (startDate) => !!startDate,
        then: (schema) =>
          schema.min(
            Yup.ref("startDate"),
            props.t("End date cannot be before Start date")
          ),
        otherwise: Yup.date().when("currentStartDate", {
          is: (currentStartDate) => !!currentStartDate,
          then: (schema) =>
            schema.min(
              Yup.ref("currentStartDate"),
              props.t("End date cannot be before project Start date")
            ),
          otherwise: Yup.date().nullable(),
        }),
      }),
    },
    ["startDate", "endDate"]
  );

  const formik = useFormik({
    initialValues: {
      ...newTaskObject,
    },
    validationSchema: taskValidation,
    validateOnMount: true,
    onSubmit: () => {
      try {
        setIsLoading(true);
        for (const item of props.deliverableCreatedList) {
          if (!formik.values.startDate && item.startDate < formik.values.currentStartDate) {
            formik.setFieldValue('startDate', props?.projectMeta?.schedule?.startDate?.new ?? "")
            return toast.error(`Deliverable ${item.name} start date(${dayjs(item.startDate).format('MM/DD/YYYY')}) is greater than project current start date`);
          }
          if (!formik.values.endDate && item.endDate > formik.values.currentEndDate) {
            formik.setFieldValue('endDate', props?.projectMeta?.schedule?.endDate?.new ?? "")
            return toast.error(`Deliverable ${item.name} end date(${dayjs(item.endDate).format('MM/DD/YYYY')}) is less than project current end date`);
          }

          if (formik.values.startDate && item.startDate < formik.values.startDate) {
            formik.setFieldValue('startDate', props?.projectMeta?.schedule?.startDate?.new ?? "")
            return toast.error(`Deliverable ${item.name} start date(${dayjs(item.startDate).format('MM/DD/YYYY')}) is greater than project requested start date`);
          }

          if (formik.values.endDate && item.endDate > formik.values.endDate) {
            formik.setFieldValue('endDate', props?.projectMeta?.schedule?.endDate?.new ?? "")
            return toast.error(`Deliverable ${item.name} end date(${dayjs(item.endDate).format('MM/DD/YYYY')}) is less than project requested end date`);
          }
        }

        for (const item of props.deliverableUpdatedList) {
          if (!formik.values.startDate && (item?.startDate && item.startDate?.new < formik.values.currentStartDate)) {
            formik.setFieldValue('startDate', props?.projectMeta?.schedule?.startDate?.new ?? "")
            return toast.error(`Deliverable ${item.name} start date(${dayjs(item.startDate.new).format('MM/DD/YYYY')}) is greater than project current start date`);
          }

          if (!formik.values.endDate && (item?.endDate && item.endDate?.new > formik.values.currentEndDate)) {
            formik.setFieldValue('endDate', props?.projectMeta?.schedule?.endDate?.new ?? "")
            return toast.error(`Deliverable ${item.name} end date(${dayjs(item.endDate.new).format('MM/DD/YYYY')}) is less than project current end date`);
          }

          if (formik.values.startDate && item?.startDate && item.startDate?.new < formik.values.startDate) {
            formik.setFieldValue('startDate', props?.projectMeta?.schedule?.startDate?.new ?? "")
            return toast.error(`Deliverable ${item.name} start date ${dayjs(item.startDate.new).format('MM/DD/YYYY')} is greater than project requested start date`);
          }

          if (formik.values.endDate && item.endDate && item.endDate?.new > formik.values.endDate) {
            formik.setFieldValue('endDate', props?.projectMeta?.schedule?.endDate?.new ?? "")
            return toast.error(`Deliverable ${item.name} end date ${dayjs(item.endDate.new).format('MM/DD/YYYY')} is less than project requested end date`);
          }
        }

        let data;
        let moduleDetails = {
          module: ChangeRequestModule.PROJECT,
          event: Action.UPDATE,
          key: "schedule",
        };

        if (formik.values.startDate === formik.values.currentStartDate && formik.values.endDate === formik.values.currentEndDate) {
          formik.setFieldValue('startDate', props?.projectMeta?.schedule?.startDate?.new ?? "")
          formik.setFieldValue('endDate', props?.projectMeta?.schedule?.endDate?.new ?? "")
          return toast.error(
            "There is no change in start date and end date!"
          );
        // } else if (formik.values.endDate === formik.values.currentEndDate) {
        //   formik.setFieldValue('endDate', props?.projectMeta?.schedule?.endDate?.new ?? "")
        //   return toast.error("Current end date and updated end date cannot be same!");
        }

        if (formik.values.startDate !== "" && formik.values.endDate !== "") {
          data = {
            schedule: {
              startDate: {
                old: formik.values.currentStartDate,
                new: dayjs(formik.values.startDate).format("YYYY-MM-DD"),
              },
              endDate: {
                old: formik.values.currentEndDate,
                new: dayjs(formik.values.endDate).format("YYYY-MM-DD"),
              },
            }
          };
        } else if (formik.values.startDate !== "") {
          data = {
            schedule: {
              startDate: {
                old: formik.values.currentStartDate,
                new: dayjs(formik.values.startDate).format("YYYY-MM-DD"),
              }
            }
          };
        } else if (formik.values.endDate !== "") {
          data = {
            schedule: {
              endDate: {
                old: formik.values.currentEndDate,
                new: dayjs(formik.values.endDate).format("YYYY-MM-DD"),
              }
            }
          };
        }
        props.validate(
          moduleDetails,
          data,
          () => {
            setIsLoading(false);
          },
          () => {
            setIsLoading(false);
            formik.setFieldValue('startDate', props?.projectMeta?.schedule?.startDate?.new ?? "")
            formik.setFieldValue('endDate', props?.projectMeta?.schedule?.endDate?.new ?? "")
          }
        );
      } catch (e) {
        // not handle any changes
      } finally {
        setIsLoading(false)
      }
    },
  });

  return (
    <div>
      <div className="changeRequest_schedule">
        <h6 className="mb-0">{props.t('Current Schedule')}</h6>
        <div className="row">
          <div className="col-md-6 changeRequest_datePicker mt-1 pe-0">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  format="MM/DD/YYYY"
                  readOnly={true}
                  className="form-control"
                  slotProps={{
                    textField: {
                      error: false,
                    },
                  }}
                  value={dayjs(formik.values?.currentStartDate)}
                  label={props.t("Start Date")}
                />
              </DemoContainer>
            </LocalizationProvider>
          </div>
          <div className="col-md-6 changeRequest_datePicker mt-1">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  format="MM/DD/YYYY"
                  readOnly={true}
                  slotProps={{
                    textField: {
                      error: false,
                    },
                  }}
                  value={dayjs(formik.values?.currentEndDate)}
                  label={props.t("End Date")}
                />
              </DemoContainer>
            </LocalizationProvider>
          </div>
        </div>
      </div>
      <div className="changeRequest_schedule">
        <h6 className="mb-0 mt-2">{props.t('Requested Schedule')}</h6>
        <div className="row">
          <div className=" col-md-6 changeRequest_datePicker mt-1 pe-0">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  label={props.t("Start Date") + ' *'}
                  className="form-control"
                  format="MM/DD/YYYY"
                  name="startDate"
                  value={formik?.values?.startDate?.toString().trim() ? dayjs(formik.values.startDate) : null}
                  slotProps={{
                    field: {
                      clearable: true,
                      onBlur: (_) => {
                        if (!formik.touched?.startDate) {
                          formik.setFieldTouched("startDate", true, false);
                        }
                      },
                    },
                    onClear: () => {
                      formik.setFieldValue("startDate", "");
                    },
                  }}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "startDate",
                      e ? dayjs(e).format("YYYY-MM-DD") : ""
                    );
                    if (!formik.touched?.startDate) {
                      formik.setFieldTouched("startDate", true, false);
                    }
                  }}
                  required
                />
              </DemoContainer>
              {formik.touched.startDate && formik.errors.startDate && (
                <span className="text-danger">{formik.errors.startDate}</span>
              )}
            </LocalizationProvider>
          </div>
          <div className="col-md-6 changeRequest_datePicker mt-1">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  label={props.t("End Date") + ' *'}
                  className="form-control"
                  format="MM/DD/YYYY"
                  name="endDate"
                  value={formik?.values?.endDate?.toString().trim() ? dayjs(formik.values.endDate) : null}
                  on
                  slotProps={{
                    field: {
                      clearable: true,
                      onBlur: (_) => {
                        if (!formik.touched?.endDate) {
                          formik.setFieldTouched("endDate", true, false);
                        }
                      },
                    },
                    onClear: () => {
                      formik.setFieldValue("endDate", "");
                    },
                  }}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "endDate",
                      e ? dayjs(e).format("YYYY-MM-DD") : ""
                    );
                    if (!formik.touched?.endDate) {
                      formik.setFieldTouched("endDate", true, false);
                    }
                  }}
                />
              </DemoContainer>
              {formik.touched.endDate && formik.errors.endDate && (
                <span className="text-danger">{formik.errors.endDate}</span>
              )}
            </LocalizationProvider>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end align-items-center mt-2">
        <button
          className={`edit_button ${isLoading ? "bg-secondary" : ""
            }`}
          disabled={isLoading}
          onClick={formik.handleSubmit}
        >
          {isLoading && (
            <Spinner className="ml-1 me-2" type="border" color="light" size="sm" />
          )}
          {props.t('Save Changes')}
        </button>
      </div>
    </div>
  );
};

export default withTranslation() (ScheduleModule);
